import { useState, useEffect } from "react";
import { Career } from "../Types/Types";

// Interface for career data


// Custom hook for managing career data
const useAdminCareer = (
  itemsPerPage: number,
  mode: "default" | "loadMore" = "default",
  activeFilter: boolean | null = null // Add activeFilter parameter
) => {  // States for handling data, loading, and errors
  const [success, setSuccess] = useState<string | null>(null);

  const [careers, setCareers] = useState<Career[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);

  // States for search, sorting, and pagination
  const [searchQuery, setSearchQuery] = useState<string>(""); // Search term
  const [sortBy, setSortBy] = useState<string>("createdAt"); // Default sort by date
  const [sortOrder, setSortOrder] = useState<string>("desc"); // Default sort order (desc)

  const apiUrl = process.env.REACT_APP_API_URL;
  const url = `${apiUrl}/api/adminCareer`;


  // Function to fetch career data with search, sort, and pagination
  const fetchCareers = async () => {
    setLoading(true);
    setError(null);

    // Get token from local storage (or another source)
    const token = localStorage.getItem("token"); // Updated to use "token" instead of "authToken"
    const lang = localStorage.getItem('lang') || 'en';

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    // If token exists, include it in the Authorization header
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      // Build the query string with activeFilter
      const queryParams = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        search: searchQuery,
        sortBy: sortBy,
        sortOrder: sortOrder,
        lang: lang
      });

      // Only add active filter if it's not null (for admin, we want to see ALL careers regardless of active status)
      if (activeFilter !== null) {
        queryParams.set('active', activeFilter.toString());
      }

      const response = await fetch(`${url}?${queryParams.toString()}`, {
        method: "GET",
        headers,
      });

      const data = await response.json();

      if (response.ok) {
        if (mode === "loadMore" && currentPage > 1) {
          setCareers((prev) => [...prev, ...data.careers]);
        } else {
          setCareers(data.careers);
        }
        setTotalCount(data.totalCount); // Set the total count for pagination
      } else {
        setError("Error fetching careers"); // Handle error
      }
    } catch (err) {
      setError("An error occurred while fetching careers"); // Catch and display errors
    } finally {
      setLoading(false); // Set loading to false after request
    }
  };

  // Effect to fetch careers when page or query changes
  useEffect(() => {
    fetchCareers();
  }, [currentPage, searchQuery, sortBy, sortOrder, itemsPerPage]); // Refetch when these change

  // Reset to page 1 when itemsPerPage changes
  useEffect(() => {
    setCurrentPage(1);
  }, [itemsPerPage]);


  const createCareer = async (newCareer: Omit<Career, "_id" | "createdAt" | "updatedAt">) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };

    // Ensure active is always an object with en and mk booleans
    let activeObj = newCareer.active;
    if (typeof activeObj === "boolean") {
      activeObj = { en: activeObj, mk: activeObj };
    }
    if (!activeObj || typeof activeObj !== "object") {
      activeObj = { en: true, mk: true };
    }
    const payload = { ...newCareer, active: activeObj };

    try {
      const response = await fetch(`${url}`, {
        method: "POST",
        headers,
        body: JSON.stringify(payload),
      });

      const errorData = !response.ok ? await response.json() : null;

      if (!response.ok) {
        throw new Error(errorData?.error || "Failed to create career");
      }
      setSuccess("Vacancy created successfully!");

      await fetchCareers();
    } catch (err: any) {
      setError(err.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };



  const updateCareer = async (id: string, formData: object) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    const headers: HeadersInit = {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };


    try {
      const response = await fetch(`${url}/${id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify(formData),  // Send the data as JSON
      });

      if (!response.ok) {
        throw new Error(`Failed to update career. Status: ${response.status}`);
      }
      setSuccess("Vacancy update successfully!");  // <--- Success message



      await fetchCareers();
    } catch (err: any) {
      setError(err.message || "An error occurred while updating");
    } finally {
      setLoading(false);
    }
  };

  const deleteCareer = async (id: string) => {
    setLoading(true);
    setError(null);

    const token = localStorage.getItem("token");

    const headers: HeadersInit = {
      "Content-Type": "application/json",
    };

    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    try {
      const response = await fetch(`${url}/${id}`, {
        method: "DELETE",
        headers,
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess("Vacancy deleted successfully!");  // <--- Success message

        await fetchCareers();
      } else {
        setError(data.error || "Error deleting career");
      }
    } catch (err) {
      setError("An error occurred while deleting the career");
    } finally {
      setLoading(false);
    }
  };


  return {
    careers,
    loading,
    error,
    currentPage,
    setCurrentPage,
    totalCount,
    searchQuery,
    setSearchQuery,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    createCareer,
    updateCareer,
    deleteCareer,
    success,
    setSuccess
  };
};

export default useAdminCareer;
